import React, { useEffect, useState } from 'react';

import amplitude from 'amplitude-js';
import { Button } from 'apps/auth/components/common/Button/Button';
import { PASSKEY_LOGIN_CLICKED, PASSKEY_LOGIN_FAILED, PASSKEY_LOGIN_SUCCESS, PASSKEY_REGISTERED } from 'apps/auth/constants/amplitude/AmplitudeEvents';
import { AUTH_SEND_MAIN_LINK } from 'apps/auth/constants/UrlConstants';
import { logWarn } from 'apps/auth/helpers/clientLogger';

import { isPasskeySupported, loginWithPasskey, registerPasskey } from '../../services/passkeyService';

interface PasskeyButtonProps {
    /** Текст кнопки, приходит из messages (i18n) */
    caption: string;
    /** 'auth' — вход через passkey (по умолчанию), 'register' — регистрация passkey */
    mode?: 'auth' | 'register';
    /** URL для редиректа после регистрации passkey (режим register) */
    redirectUrl?: string;
    toggleSpinner?: (show: boolean) => void;
}

const getDeviceName = (): string => {
    const ua = navigator.userAgent;
    if (/iPhone/.test(ua)) return 'iPhone';
    if (/iPad/.test(ua)) return 'iPad';
    if (/Android/.test(ua)) return 'Android';
    if (/Mac/.test(ua)) return 'Mac';
    if (/Windows/.test(ua)) return 'Windows';
    return 'Unknown device';
};

const PasskeyButton: React.FC<PasskeyButtonProps> = ({ caption, mode = 'auth', redirectUrl, toggleSpinner }) => {
    const [supported, setSupported] = useState(false);

    useEffect(() => {
        setSupported(isPasskeySupported());
    }, []);

    if (!supported) return null;

    const handleAuth = async () => {
        toggleSpinner?.(true);
        amplitude.getInstance().logEvent(PASSKEY_LOGIN_CLICKED);

        try {
            const result = await loginWithPasskey();
            amplitude.getInstance().logEvent(PASSKEY_LOGIN_SUCCESS);
            window.location.href = result.redirectTo;
        } catch (err: unknown) {
            toggleSpinner?.(false);
            // NotAllowedError — пользователь закрыл диалог, не показываем ошибку
            if (err instanceof Error && err.name !== 'NotAllowedError') {
                amplitude.getInstance().logEvent(PASSKEY_LOGIN_FAILED);
                logWarn(err);
            }
        }
    };

    const handleRegister = async () => {
        toggleSpinner?.(true);

        try {
            await registerPasskey(getDeviceName());
            amplitude.getInstance().logEvent(PASSKEY_REGISTERED);
            window.location.href = redirectUrl || AUTH_SEND_MAIN_LINK;
        } catch (err: unknown) {
            toggleSpinner?.(false);
            if (err instanceof Error && err.name !== 'NotAllowedError') {
                logWarn(err);
            }
        }
    };

    return (
        <Button
            variant="row"
            textCenter
            onClick={mode === 'register' ? handleRegister : handleAuth}
            caption={caption}
            id={mode === 'register' ? 'passkey_register_button' : 'passkey_login_button'}
        />
    );
};

export default PasskeyButton;
